import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { TypeOptions } from '../summary/editSummary';
import { Button } from '@mui/material';
import moment from 'moment';
import { ChildrenCost, Costing, CustomCost, formatCurrency, MaterialCost } from 'pdf-generator';
import { CostingTypes, findFirstCustomCosts } from '../utils';
import { Download } from '@mui/icons-material';
import { getUserSelectedCurrency } from '../../../utils';
import { useAppSelector } from '../../../redux';

const ExcelGenerator = ({ data, pricingID, levelLimit }: { data: Costing, pricingID: string, levelLimit: number }) => {
  const { auth } = useAppSelector(store => store);

  const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

  const base_currency = data.pricing_config?.exchange_rates?.base_currency ?? DEFAULT_CURRENCY;

  const generateExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    // Sheet 1: BOM Cost Tree Structure
    const bomSheet = workbook.addWorksheet('BOM Cost Structure');

    // Merge Cells
    bomSheet.mergeCells('C1:D1');
    bomSheet.mergeCells('E1:F1');
    bomSheet.mergeCells('G1:H1');
    bomSheet.mergeCells('I1:J1');
    bomSheet.mergeCells('K1:L1');

    // Set up columns
    bomSheet.columns = [
      { header: 'Tree View', key: 'treeView', width: 50 },  // Increased width for better visibility
      { header: 'Description', key: 'description', width: 40 },
      { header: 'Total Overhead/Activity Cost', key: 'totalOverheadCost', width: 40 },
      { header: 'Total Overhead/Activity Cost', key: 'totalOverheadCurrency', width: 40 },
      { header: 'Total Child BOM Cost', key: 'totalChildBOMCost', width: 40 },
      { header: 'Total Child BOM Cost', key: 'totalChildBOMCurrency', width: 40 },
      { header: 'Total Child Material Cost', key: 'totalChildMaterialCost', width: 40 },
      { header: 'Total Child Material Cost', key: 'totalChildMaterialCurrency', width: 40 },
      { header: 'Total BOM Cost', key: 'totalBOMCost', width: 40 },
      { header: 'Total BOM Cost', key: 'totalBOMCurrency', width: 40 },
      { header: 'Material Cost', key: 'totalMaterialCost', width: 40 },
      { header: 'Material Cost', key: 'totalMaterialCurrency', width: 40 },
      { header: 'Override Cost', key: 'overrideCost', width: 40 },
      { header: 'Override Currency', key: 'overrideCurrency', width: 40 },
      { header: 'Spare Part Qty', key: 'sparePartQty', width: 40 },
      { header: 'Quantity', key: 'normalQty', width: 40 },
      { header: 'Total Quantity', key: 'totalQty', width: 40 },
      { header: 'Total Cost', key: 'totalCost', width: 40 },
      { header: 'Total Cost Currency', key: 'totalCostCurrency', width: 40 },
      ...(findFirstCustomCosts(data?.calculated_cost) || []).map((c: CustomCost) => {
        return ({ header: `${c.description} ${c?.subtract === true ? "(-)" : "(+)"}`, key: c.description, width: 40 })
      }),
      { header: 'Markup in (%)', key: 'markupPercentage', width: 40 },
      { header: 'New Total Cost (BOM Currency)', key: 'parentCost', width: 60 },
      { header: 'BOM Currency', key: 'bomCurrency', width: 40 },
      { header: 'New Total Cost (Pricing Currency)', key: 'markupCost', width: 60 },
      { header: 'Currency', key: 'totalCurrency', width: 40 },
    ];

    // Style the header row
    bomSheet.getRow(1).font = { bold: true };
    bomSheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFd1c4e9' },
    };
    bomSheet.getRow(1).font = {
      // color: { argb: 'FFFFFFFF' }, 
      bold: true
    };

    let rowIndex = 2;

    const addBomRow = (item: any, level: string = '1', depth: number = 0, currentLeveling: number, default_custom_costs: CustomCost[]) => {
      const row = bomSheet.getRow(rowIndex);

      // Tree View column with indentation
      const treeViewCell = row.getCell(1);
      treeViewCell.value = `${'  '.repeat(depth)}${level} [${item.sequence_id}-r${item.version}]`;
      treeViewCell.alignment = { indent: depth };  // This adds left indentation

      // Other columns
      row.getCell(2).value = item.short_description || '';
      row.getCell(3).value = item.sum_of_assm_procs_final_total_cost_in_parent_currency?.cost;
      row.getCell(4).value = item.sum_of_assm_procs_final_total_cost_in_parent_currency?.currency;
      row.getCell(5).value = item.sum_of_children_final_total_cost_in_parent_currency?.cost;
      row.getCell(6).value = item.sum_of_children_final_total_cost_in_parent_currency?.currency;
      row.getCell(7).value = item.sum_of_material_final_total_cost_in_parent_currency?.cost;
      row.getCell(8).value = item.sum_of_material_final_total_cost_in_parent_currency?.currency;
      row.getCell(9).value = item.bom_cost?.cost;
      row.getCell(10).value = item.bom_cost?.currency;
      row.getCell(11).value = item.material_cost?.cost;
      row.getCell(12).value = item.material_cost?.currency;
      row.getCell(13).value = item.override_cost || 0;
      row.getCell(14).value = item.bom_cost?.currency || item.material_cost?.currency || DEFAULT_CURRENCY;
      row.getCell(15).value = item.spare_part_quantity;
      row.getCell(16).value = item.normal_quantity || 1;
      row.getCell(17).value = item.total_quantity || 1;
      row.getCell(18).value = item.total_cost ? item.total_cost.cost : 0;
      row.getCell(19).value = item.total_cost ? item.total_cost.currency : DEFAULT_CURRENCY;
      let count = 20;
      const current_custom = item?.custom_costs?.length > 0 ? item?.custom_costs : item?.assembly_process_costs?.[0]?.custom_costs?.length > 0 ? item?.assembly_process_costs?.[0]?.custom_costs : custom_costs_default || [];
      current_custom?.forEach((c: CustomCost, idx: number) => {
        const cC = item?.custom_costs?.[idx] || item?.assembly_process_costs?.[0]?.custom_costs?.[idx];
        row.getCell(count).value = cC?.value
        count += 1
      })

      row.getCell(count).value = item?.assembly_process_costs?.[0]?.markup_percentage || item?.markup_percentage;
      row.getCell(count + 1).value = item?.final_total_cost_in_parent_currency?.cost || item?.final_total_cost?.cost;
      row.getCell(count + 2).value = item?.final_total_cost_in_parent_currency?.currency || item?.final_total_cost?.currency;
      row.getCell(count + 3).value = item?.final_total_cost_in_pricing_currency?.cost;
      row.getCell(count + 4).value = item?.final_total_cost_in_pricing_currency?.currency;

      rowIndex++;

      // Recursively add child rows
      if (item.children_costs) {
        if (levelLimit === currentLeveling) {
          return;
        }
        item.children_costs.forEach((child: ChildrenCost, index: number) => {
          const childLevel = `${level}.${index + 1}`;
          addBomRow(child, childLevel, depth + 2, currentLeveling + 1, custom_costs_default);
        });
      }
      if (item.material_costs) {
        if (levelLimit === currentLeveling) {
          return;
        }
        item.material_costs.forEach((material: MaterialCost, index: number) => {
          const materialLevel = `${level}.${item.children_costs ? item.children_costs.length + index + 1 : index + 1}`;
          addBomRow(material, materialLevel, depth + 2, currentLeveling, custom_costs_default);
        });
      }
    };

    const custom_costs_default = (findFirstCustomCosts(data.calculated_cost) || []);

    // Add BOM rows
    addBomRow(data.calculated_cost, '1', 0, 1, custom_costs_default);

    // Set column widths
    bomSheet?.columns?.forEach((column: any, index) => {
      let maxLength = 0;
      column?.eachCell({ includeEmpty: true }, (cell: any) => {
        maxLength = Math.max(maxLength, cell.value ? cell.value.toString().length : 0);
      });
      column.width = Math.min(maxLength + 2, 40);
    });

    if (data?.pricing_config?.cost_groups?.some(c => c?.costs?.length > 0)) {
      // Sheet 2: Cost Group Items
      const costGroupItemsSheet = workbook.addWorksheet('Cost Group Items');
  
      // Helper function to add styled headers
      const addStyledItemHeader = (rowIndex: number, header: any) => {
        const headerRow = costGroupItemsSheet.getRow(rowIndex);
        headerRow.getCell(1).value = header;
        headerRow.getCell(1).font = { bold: true };
        headerRow.getCell(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFd1c4e9' },
        };
        headerRow.getCell(1).font = {
          // color: { argb: 'FFFFFFFF' }, 
          bold: true
        };
      };
  
      rowIndex = 1
  
      data?.pricing_config?.cost_groups?.forEach((cg) => {
        if (cg?.costs?.length > 0) {
  
          // Cost Groups section
          addStyledItemHeader(rowIndex, cg.title);
          rowIndex += 3
          costGroupItemsSheet.getRow(rowIndex).values = ['Description', 'Units', 'Value', 'Quantity', `Total Amount`];
          costGroupItemsSheet.getRow(rowIndex).font = { bold: true };
  
          rowIndex += 1;
          cg?.costs?.forEach((group) => {
            const row = costGroupItemsSheet.getRow(rowIndex);
            row.values = [
              group.description,
              CostingTypes.find(c => c.value === group.units)?.label || group.units,
              group.value || 0,
              group.quantity,
              group.total_cost?.toFixed(2),
            ];
            rowIndex++;
          });
  
          rowIndex += 2;
        }
      })
  
      // Set column widths
      costGroupItemsSheet?.columns?.forEach((column: any, index) => {
        let maxLength = 0;
        column?.eachCell({ includeEmpty: true }, (cell: any) => {
          maxLength = Math.max(maxLength, cell.value ? cell.value.toString().length : 0);
        });
        column.width = Math.min(maxLength + 2, 30);
      });
    }


    // Sheet 3: Cost Groups and Cost Items
    const costGroupsSheet = workbook.addWorksheet('Master Pricing');

    // Helper function to add styled headers
    const addStyledHeader = (rowIndex: number, header: any) => {
      const headerRow = costGroupsSheet.getRow(rowIndex);
      headerRow.getCell(1).value = header;
      headerRow.getCell(1).font = { bold: true };
      headerRow.getCell(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFd1c4e9' },
      };
      headerRow.getCell(1).font = { 
        // color: { argb: 'FFFFFFFF' }, 
        bold: true 
      };
    };

    // Cost Groups section
    addStyledHeader(1, 'Major Items');
    costGroupsSheet.getRow(3).values = ["Item Number", 'Description', 'Value', 'Discount Percentage', 'Quantity', `Total Amount (${base_currency})`];
    costGroupsSheet.getRow(3).font = { bold: true };

    rowIndex = 4;
    const row = costGroupsSheet.getRow(rowIndex);
    row.values = [
      `${data.calculated_cost?.sequence_id ?? ""}-r${data.calculated_cost?.version ?? 0}`,
      data.calculated_cost?.short_description ?? "",
      data.calculated_cost?.unit_cost ?? 0,
      data.pricing_config?.bom_cost_discount_percentage ?? 0,
      data.pricing_config?.bom_cost_quantity ?? 1,
      Number(((data.calculated_cost?.total_cost?.cost ?? 0) * (data.pricing_config?.bom_cost_quantity ?? 1) * (1 - (data.pricing_config?.bom_cost_discount_percentage ?? 0) / 100)).toFixed(2)),
    ];
    rowIndex++;
    data?.pricing_config?.cost_groups?.forEach((group) => {
      const row = costGroupsSheet.getRow(rowIndex);
      const currentValue = group?.costs?.reduce(
        (acc: number, item: { total_cost: number }) =>
          acc + item.total_cost,
        0
      );
      row.values = [
        "N/A",
        group.title,
        currentValue ?? 0,
        group.discount_percentage || 0,
        group.quantity,
        Number((group.total_amount ?? 0).toFixed(2)),
      ];
      rowIndex++;
    });

    // // Total Cost before Discount and Tax
    // costGroupsSheet.getRow(rowIndex).values = ['', 'Total Cost before Discount and Tax (Gross Cost)', '', '', '', data.gross_cost];
    // costGroupsSheet.getRow(rowIndex).font = { bold: true };

    rowIndex += 2;

    if (data.pricing_config?.other_costs?.length > 0) {
      // Other Items section
      addStyledHeader(rowIndex, 'Other Items');
      rowIndex += 2;
      costGroupsSheet.getRow(rowIndex).values = ['Description', 'Type', 'Value', `Total Amount (${base_currency})`];
      costGroupsSheet.getRow(rowIndex).font = { bold: true };
      rowIndex++;
  
      data.pricing_config?.other_costs.forEach((cost, index) => {
        const row = costGroupsSheet.getRow(rowIndex);
        row.values = [cost.description, TypeOptions.find((t) => t.value === cost.type)?.label ?? "", cost.value ?? 0, Number((cost.amount ?? 0).toFixed(2))];
        rowIndex++;
      });
  
      rowIndex += 2;
    }

    if (data.pricing_config?.discounts?.length > 0) {
      // Adjustments section
      addStyledHeader(rowIndex, 'Adjustments');
      rowIndex += 2;
      costGroupsSheet.getRow(rowIndex).values = ['Description', 'Type', 'Value', `Total Amount (${base_currency})`];
      costGroupsSheet.getRow(rowIndex).font = { bold: true };
      rowIndex++;
  
      data.pricing_config?.discounts.forEach((discount, index) => {
        const row = costGroupsSheet.getRow(rowIndex);
        row.values = [discount.description, TypeOptions.find((t) => t.value === discount.type)?.label ?? "", discount.value ?? 0, Number((discount.amount ?? 0).toFixed(2))];
        rowIndex++;
      });
  
      // // Total Discounts and Total Price before tax
      // costGroupsSheet.getRow(rowIndex).values = ['', 'Total Discounts', '', '', data.total_discount];
      // costGroupsSheet.getRow(rowIndex).font = { bold: true };
      // rowIndex++;
      // costGroupsSheet.getRow(rowIndex).values = ['', 'Total Price before tax', '', '', data.gross_cost_after_discount];
      // costGroupsSheet.getRow(rowIndex).font = { bold: true };
      rowIndex += 2;
    }

    if (data.pricing_config?.taxes?.length > 0) {
      // Taxes section
      addStyledHeader(rowIndex, 'Taxes');
      rowIndex += 2;
      costGroupsSheet.getRow(rowIndex).values = ['Description', 'Type', 'Value', `Total Amount (${base_currency})`];
      costGroupsSheet.getRow(rowIndex).font = { bold: true };
      rowIndex++;
  
      data.pricing_config?.taxes.forEach((tax, index) => {
        const row = costGroupsSheet.getRow(rowIndex);
        row.values = [tax.description, TypeOptions.find((t) => t.value === tax.type)?.label ?? "", tax.value ?? 0, Number((tax.amount ?? 0).toFixed(2))];
        rowIndex++;
      });
  
      // // Total Tax and Final Quote Price
      // costGroupsSheet.getRow(rowIndex).values = ['', 'Total Tax', '', '', data.total_tax];
      // costGroupsSheet.getRow(rowIndex).font = { bold: true };
      // rowIndex++;
      // const finalQuotePriceRow = costGroupsSheet.getRow(rowIndex);
      // finalQuotePriceRow.values = ['', 'Final Quote Price', '', '', data.final_price];
      // finalQuotePriceRow.font = { bold: true };
      // finalQuotePriceRow.eachCell((cell) => {
      //   cell.fill = {
      //     type: 'pattern',
      //     pattern: 'solid',
      //     fgColor: { argb: 'FFFFFF00' },
      //   };
      // });
      rowIndex += 2;
    }

    // Summary section
    addStyledHeader(rowIndex, 'Summary');
    rowIndex += 2;
    costGroupsSheet.getRow(rowIndex).values = ['', 'Total Discounts', formatCurrency(Number((data.total_discount ?? 0).toFixed(2)), base_currency, false)];
    rowIndex++;
    costGroupsSheet.getRow(rowIndex).values = ['', 'Total Tax', formatCurrency(Number((data.total_tax ?? 0).toFixed(2)), base_currency, false)];
    rowIndex++;
    const summaryFinalRow = costGroupsSheet.getRow(rowIndex);
    summaryFinalRow.values = ['', 'Final Quote Price', formatCurrency(Number((data.final_price ?? 0).toFixed(2)), base_currency, false)];
    summaryFinalRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF00' },
      };
    });

    // Set column widths
    costGroupsSheet?.columns?.forEach((column: any, index) => {
      let maxLength = 0;
      column?.eachCell({ includeEmpty: true }, (cell: any) => {
        maxLength = Math.max(maxLength, cell.value ? cell.value.toString().length : 0);
      });
      column.width = Math.min(maxLength + 2, 30);
    });

    // Generate and save the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `BOM_Cost_Summary (${pricingID} [${moment(data?.created_time).format("lll")}]).xlsx`);
  };

  return (
    <Button variant='contained' onClick={generateExcel} startIcon={<Download />}>Excel</Button>
  );
};

export default ExcelGenerator;